/*this file responsible for :
- selecting and trajectories of the object;
- colour select;
- selecting manual or auto colour change;
*/

import { TaskBallColor } from '../../../../../commands/practitioner/enums/TaskBallColor';
import { ATask, ATaskConfiguration, ITaskConfiguration } from './TaskDefinitions';
import { RandomNumberGenerator } from '../scene/RandomNumberGenerator';
// @ts-ignore
import { SubEvent } from 'sub-events';
import { ColorSwitchOptions } from '../../../../../commands/practitioner/enums/ColorSwitchOptions';
import TaskScene, { TaskTheme } from '../scene/TaskScene';
import CONSTANTS from '../../../CONSTANTS/CONSTANTS';
import { SphereTrajectoryOption } from '../../../../../commands/practitioner/enums/SphereTrajectoryOption';
// @ts-ignore
import Phaser from 'phaser';
import { defaultBallColors, legacyDefaultColors } from '../../../../../commands/practitioner/ballColors/BallColors';

type RandomDataGenerator = RandomNumberGenerator;

function hexLabelToNumber(label: string): number {
  let hex = label.slice(1);
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(c => c + c)
      .join('');
  }
  return parseInt(hex, 16);
}

const LEGACY_BALL_NUMBERS: number[] = legacyDefaultColors.map(({ label }) => hexLabelToNumber(label));
const ALL_BALL_NUMBERS = defaultBallColors.map(({ rgb }) => (rgb.r << 16) | (rgb.g << 8) | rgb.b);

export class MovingTargetTaskConfiguration extends ATaskConfiguration {
  SphereTrajectoryOption: SphereTrajectoryOption = SphereTrajectoryOption.Bouncing;
  colour: number = 0xffffff;
  useLegacyColors: boolean = false;
  isAutomatically: ColorSwitchOptions = ColorSwitchOptions.Manually;
  baseSpeed: number = CONSTANTS.movingTarget.baseSpeed;
  speedModifier: number = CONSTANTS.movingTarget.speedModifier;
  theme: TaskTheme;

  /** The global sprite key lookup to sprites */
  spriteKeyToSprite: Map<string, Phaser.GameObjects.Sprite> = new Map<string, Phaser.GameObjects.Sprite>();

  constructor(theme: TaskTheme, seed: number) {
    super(seed);
    this.theme = theme;
  }

  public Copy(): MovingTargetTaskConfiguration {
    let config = new MovingTargetTaskConfiguration(this.theme, this.seed);
    config.SphereTrajectoryOption = this.SphereTrajectoryOption;
    config.colour = this.colour;
    config.isAutomatically = this.isAutomatically;
    config.spriteKeyToSprite = this.spriteKeyToSprite;
    config.baseSpeed = this.baseSpeed;
    config.speedModifier = this.speedModifier;
    config.seed = this.seed;
    config.enabled = this.enabled;
    config.useLegacyColors = this.useLegacyColors;
    return config;
  }

  ApplyDifficulty(difficultyConfig: ITaskConfiguration): void {
    let difConfig = difficultyConfig as MovingTargetTaskConfiguration;
    this.speedModifier = difConfig.speedModifier;
  }
}

export abstract class TimelineTask<Configuration extends ITaskConfiguration> extends ATask<Configuration> {
  timelines: Map<string, Phaser.Tweens.Timeline>;
  garbage: Array<Phaser.Tweens.Timeline>;
  text?: Phaser.GameObjects.Text;
  isVisible: boolean = false;

  constructor(configuration: Configuration, scene: Phaser.Scene, name: string) {
    super(configuration, scene, name);
    this.timelines = new Map<string, Phaser.Tweens.Timeline>();
    this.garbage = new Array<Phaser.Tweens.Timeline>();
  }

  StartTimelines(scene: Phaser.Scene) {
    // console.log(`[Task::StartTimelines] ${this.name}`);
    this.timelines.forEach((timeline, key) => {
      if (timeline.state === Phaser.Tweens.PAUSED) {
        // console.log(`[Task::Resume Timeline] ${this.name} ${key}`);
        timeline.resume();
      } else {
        // console.log(`[Task::Play Timeline] ${this.name} ${key}`);
        timeline.play();
      }
    });
  }

  StopTimelines(scene: Phaser.Scene) {
    this.timelines.forEach((timeline, key) => {
      timeline.pause();
    });
  }

  Start(scene: Phaser.Scene) {
    this.StartTimelines(scene);
    this.SetVisible(true);
    this.isActive = true;

    this.random = new RandomNumberGenerator(this.seed);
    this.RandomUpdated();

    if (!this.text) {
      return;
    } // Move to the specific task (or create a TimelineTextTask that inherits from TimelineTask)
    this.text.setVisible(false);
  }

  Stop(scene: Phaser.Scene) {
    this.StopTimelines(scene);
    this.SetVisible(false);
    this.isActive = false;
    return;
  }

  // Stops any existing timeline, and stores a new one
  ReplaceTimeline(key: string, timeline: Phaser.Tweens.Timeline) {
    // console.log(
    //   `[Task::ReplaceTimeline] Replacing timeline on ${
    //     this.name
    //   } for timeline ${key} - existed prior: ${this.timelines.has(key)}`
    // );

    let oldTimeline = this.timelines.get(key);
    if (oldTimeline != null) {
      oldTimeline.pause(); // immediately stop it
      this.garbage.push(oldTimeline);
    }

    let overrider: any = timeline;
    overrider.name = key;

    this.timelines.set(key, timeline);

    // Start the new given timeline if we are active
    if (this.isActive) {
      // console.log(`[Task::ReplaceTimeline] Playing timeline because active on ${this.name} for timeline ${key}`);
      timeline.play();
    }
  }

  /**
   * It looks like Phaser 3 has a bug where if we attempt to start and stop a timeline in the same frame, it keeps playing.
   * Therefore we attempt to manually force stop the timelines every frame.
   */
  AttemptToCleanGarbage() {
    // Every frame, see if the timeline is already stopped, if so, remove it, else attempt to stop and wait a frame.
    //this.garbage = this.garbage.filter(value => value.isPlaying());
    this.garbage.forEach(value => value.stop());
    this.garbage.length = 0;
  }

  Render() {
    this.AttemptToCleanGarbage();
  }

  // Gets the timeline stored under the given key.
  GetTimeline(key: string) {
    return this.timelines.get(key);
  }

  UpdateSpeedForTimeline(timeline: Phaser.Tweens.Timeline | undefined, configuration: MovingTargetTaskConfiguration) {
    if (!timeline) {
      return;
    }
    let stepModifier = 0.1;
    let newSpeed = (configuration.speedModifier + stepModifier) * stepModifier;

    timeline.setTimeScale(newSpeed);
  }

  // Stops any existing timeline, and stores a new one
  RemoveTimeline(key: string) {
    this.timelines.get(key)?.stop();
    this.timelines.delete(key);
  }

  // Should make the task visible
  SetVisible(visible: boolean) {
    // console.log(`[Task::SetVisible] ${this.name} to ${visible}`);
    this.isVisible = true;
    this.OnSetVisible(visible);
  }

  abstract OnSetVisible(visible: boolean): void;
}

export class ColourInputEvent {
  public readonly Task: MovingTargetTask;

  constructor(task: MovingTargetTask) {
    this.Task = task;
  }
}

export class MovingTargetTask extends TimelineTask<MovingTargetTaskConfiguration> {
  public static readonly MOVEMENT_TIMELINE = 'movement';
  public static readonly COLOUR_TIMELINE = 'colour';
  public static readonly FADEOUT = 'fadeout';

  // State variables
  position: Phaser.Math.Vector2 = new Phaser.Math.Vector2(0, 0);
  size: number = 1;
  pointOnEllipse: number = 0;

  // Animation variables: Set once we start the animation
  image?: Phaser.GameObjects.Sprite;
  readonly OnColourChangeExpected: SubEvent<ColourInputEvent> = new SubEvent();

  private lastColorUsed?: number;

  colourRandom: RandomDataGenerator;

  constructor(configuration: MovingTargetTaskConfiguration, scene: Phaser.Scene) {
    super(configuration, scene, 'MovingTargetTask');
    this.colourRandom = new RandomNumberGenerator(this.seed);
  }

  // Responsible for setting up resources used by Task
  Create(configuration: MovingTargetTaskConfiguration, scene: Phaser.Scene): void {
    this.OnUpdate(configuration, this.configuration, scene, true);
    this.CenterImagePosition(scene);
    this.SetVisible(false);
    this.image?.setVisible(false);
  }

  Start(scene: Phaser.Scene) {
    let { width, height } = this.GetCurrenScreenSize(scene);
    if (this.isActive) {
      super.Stop(scene);
    }
    this.CenterImagePosition(scene);
    this.image?.setAlpha(1);
    this.image?.setScale(this.CalculateScalingFactor(width * 0.85, height * 0.85));
    let movement = this.CreateMovementTween(this.configuration, scene);
    this.ReplaceTimeline(MovingTargetTask.MOVEMENT_TIMELINE, movement);

    super.Start(scene);
  }

  Stop(scene: Phaser.Scene) {
    // Create the fade out here
    let movement = this.CreateFadeOutAndBackForCenter(this.configuration, scene);
    this.ReplaceTimeline(MovingTargetTask.MOVEMENT_TIMELINE, movement);

    // We call the super.Stop() on the fadeout timeline
  }

  RandomUpdated() {
    this.colourRandom = new RandomNumberGenerator(this.seed);
    this.pointOnEllipse = 0;
  }

  OnUpdate(
    oldConfiguration: MovingTargetTaskConfiguration,
    configuration: MovingTargetTaskConfiguration,
    scene: Phaser.Scene,
    firstCreateCall: boolean
  ) {
    let changedToManualColor =
      firstCreateCall || (!configuration.isAutomatically && !!this.configuration.isAutomatically);
    let colourChanged = firstCreateCall || this.configuration.colour !== configuration.colour;
    let themeChanged = firstCreateCall || this.configuration.theme !== configuration.theme;
    let themeChangedAndAutoColourIsOf = themeChanged && configuration.isAutomatically === ColorSwitchOptions.Manually;
    let themeChangedAndAutoColourIsOn =
      themeChanged && configuration.isAutomatically === ColorSwitchOptions.Automatically;
    let SphereTrajectoryOptionChanged =
      firstCreateCall || oldConfiguration.SphereTrajectoryOption !== configuration.SphereTrajectoryOption;

    if (SphereTrajectoryOptionChanged) {
      let movement = this.ChangeSphereTrajectoryOptionTransition(configuration, scene);
      this.ReplaceTimeline(MovingTargetTask.MOVEMENT_TIMELINE, movement);
    }

    if (oldConfiguration.isAutomatically !== configuration.isAutomatically || themeChangedAndAutoColourIsOn) {
      if (configuration.theme.name !== oldConfiguration.theme.name) {
        this.RemoveTimeline(MovingTargetTask.COLOUR_TIMELINE);
        this.UpdateColorOfImage(configuration, configuration.colour, scene);
      }
      const timeline = this.AutoImageColorUpdate(configuration, scene);
      this.ReplaceTimeline(MovingTargetTask.COLOUR_TIMELINE, timeline);
    }

    if (firstCreateCall || changedToManualColor || colourChanged || themeChangedAndAutoColourIsOf) {
      this.RemoveTimeline(MovingTargetTask.COLOUR_TIMELINE);
      this.UpdateColorOfImage(configuration, configuration.colour, scene);
    }

    this.UpdateSpeedForTimeline(this.timelines.get(MovingTargetTask.MOVEMENT_TIMELINE), configuration);
  }

  CreateFadeOutAndBackForCenter(configuration: MovingTargetTaskConfiguration, scene: Phaser.Scene) {
    this.SetVisible(true);
    this.isActive = true;
    let timeline = scene.tweens.createTimeline();

    if (!this.image) {
      return timeline;
    }

    let center = this.GetCenterPosition(scene);
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };
    let path = new Phaser.Curves.Path();

    path.moveTo(this.image.x, this.image.y);
    path.lineTo(center.x, center.y);

    timeline.add({
      targets: follower,
      t: { from: 0, to: 1 },
      ease: 'Cubic.easeInOut',
      duration: CONSTANTS.movingTarget.fadeout,
      repeat: 0,
      onUpdate: () => {
        path.getPoint(follower.t, follower.vec);
        this.image?.setPosition(follower.vec.x, follower.vec.y);
        this.image?.setAlpha(1 - follower.t);
      },
      onComplete: () => {
        super.Stop(scene);
      },
    });

    return timeline;
  }

  OnSetVisible(active: boolean): void {
    if (!this.image) {
      return;
    }
    this.image.setVisible(active);
  }

  CalculateScalingFactor(screenWidth: number, screenHeight: number, themeNameOverride?: string) {
    const referenceWidth = 1920;
    const referenceHeight = 1080;
    let baseScale = 0.2;

    const themeName = themeNameOverride ?? this.configuration.theme?.name;

    if (themeName === 'child') {
      baseScale *= 1.5;
    }

    const widthScale = screenWidth / referenceWidth;
    const heightScale = screenHeight / referenceHeight;

    const averageScale = (widthScale + heightScale) / 2;

    return baseScale * averageScale;
  }

  CalculateSpeedMultiplier(screenWidth: number, screenHeight: number) {
    const referenceWidth = CONSTANTS.baseScreenWidth;
    const referenceHeight = CONSTANTS.baseScreenHeight;
    let baseSpeed = CONSTANTS.movingTarget.bouncingSpeedMultiplier;

    let widthScale = screenWidth / referenceWidth;
    let heightScale = screenHeight / referenceHeight;
    let averageScale = (widthScale + heightScale) / 2;

    return baseSpeed * averageScale;
  }

  UpdateColorOfAutoImage(configuration: MovingTargetTaskConfiguration, scene: Phaser.Scene) {
    const palette = configuration.useLegacyColors ? LEGACY_BALL_NUMBERS : ALL_BALL_NUMBERS;

    const idx = this.colourRandom.between(0, palette.length);
    const randomColor = palette[idx];

    this.UpdateColorOfImage(configuration, randomColor, scene);
  }

  UpdateColorOfImage(configuration: MovingTargetTaskConfiguration, tintColor?: number, scene?: Phaser.Scene) {
    let oldPos = new Phaser.Math.Vector2();
    let previousScale: { x: number; y: number } | undefined;

    if (this.image) {
      previousScale = { x: this.image.scaleX, y: this.image.scaleY };
      this.image.setVisible(false);
      oldPos.set(this.image.x, this.image.y);
    }

    const isChildTheme = configuration.theme?.name === 'child';
    const applySoftTintForChild = (color: number): number => {
      if (!isChildTheme) return color;
      const intensity = 0.8;
      const rgb = Phaser.Display.Color.IntegerToRGB(color);
      const r = Math.round(rgb.r * intensity + 255 * (1 - intensity));
      const g = Math.round(rgb.g * intensity + 255 * (1 - intensity));
      const b = Math.round(rgb.b * intensity + 255 * (1 - intensity));
      return Phaser.Display.Color.GetColor(r, g, b);
    };

    let finalTint: number;
    if (configuration.useLegacyColors) {
      const enumValue = configuration.colour as TaskBallColor;
      const colourKey = TaskBallColor[enumValue];
      const imageKey =
        configuration.theme.colourNameToSpriteKey.get(colourKey) ??
        Array.from(configuration.theme.colourNameToSpriteKey.values())[0]!;
      finalTint = configuration.theme.colourNameToColourNumber.get(colourKey) ?? 0xffffff;

      this.OnColourChangeExpected.emit(new ColourInputEvent(this));
      const nextImage = configuration.spriteKeyToSprite.get(imageKey)!;
      this.image = nextImage;
      const colorToApply = configuration.isAutomatically
        ? applySoftTintForChild(tintColor!)
        : applySoftTintForChild(finalTint);
      this.image.setTint(colorToApply);
    } else {
      // new color approach
      finalTint = tintColor != null ? tintColor : configuration.colour;
      const spriteKey = configuration.theme.spriteKeyPostfix;
      const nextImage = configuration.spriteKeyToSprite.get(spriteKey)!;
      this.image = nextImage;

      if (this.lastColorUsed !== finalTint) {
        this.OnColourChangeExpected.emit(new ColourInputEvent(this));
        this.lastColorUsed = finalTint;
      }
      this.image.setTint(applySoftTintForChild(finalTint));
    }

    if (scene) {
      const { width, height } = this.GetCurrenScreenSize(scene);
      const scale = this.CalculateScalingFactor(width * 0.85, height * 0.85, configuration.theme?.name);
      this.image.setScale(scale);
    } else if (previousScale) {
      this.image.setScale(previousScale.x, previousScale.y);
    }

    this.image.setFrame(0);
    this.image.setVisible(this.isActive);
    this.image.setPosition(oldPos.x, oldPos.y);
  }

  ChangeSphereTrajectoryOptionTransition(
    configuration: MovingTargetTaskConfiguration,
    scene: Phaser.Scene
  ): Phaser.Tweens.Timeline {
    let timeline = scene.tweens.createTimeline();

    if (!this.image) {
      return timeline;
    }

    let center = this.GetCenterPosition(scene);

    let follower = { t: 0, vec: new Phaser.Math.Vector2(center.x, center.y) };
    let path = new Phaser.Curves.Path(center.x, center.y);

    /*
        if SphereTrajectoryOption equal to ellipse, then we create new ellipse and move object to the start position of ellipse.
        otherwise we just move object to center
        */
    if (configuration.SphereTrajectoryOption === SphereTrajectoryOption.Ellipse) {
      let radiusX = CONSTANTS.movingTarget.ellipseRadiusX;
      let radiusY = CONSTANTS.movingTarget.ellipseRadiusY;
      let ellipse = new Phaser.Curves.Path(center.x + radiusX, center.y);
      ellipse.ellipseTo(radiusX, radiusY);

      let getStartPositionOfEllipse = ellipse.getStartPoint();

      path.moveTo(this.image.x, this.image.y);
      path.lineTo(getStartPositionOfEllipse.x, getStartPositionOfEllipse.y);
    } else {
      path.moveTo(this.image.x, this.image.y);
      path.lineTo(center.x, center.y);
    }

    timeline.add({
      targets: follower,
      t: { from: 0, to: 1 },
      ease: 'Cubic.easeInOut',
      duration: CONSTANTS.movingTarget.trajectoryTransition,
      repeat: 0,
      onUpdate: () => {
        // On Start / Resume we need to ensure the ball is visible.
        this.image?.setAlpha(1);

        path.getPoint(follower.t, follower.vec);
        this.image?.setPosition(follower.vec.x, follower.vec.y);
      },

      onComplete: () => {
        const latestConfiguration = this.configuration;
        let movement = this.CreateMovementTween(latestConfiguration, scene);
        this.ReplaceTimeline(MovingTargetTask.MOVEMENT_TIMELINE, movement);

        this.UpdateSpeedForTimeline(this.timelines.get(MovingTargetTask.MOVEMENT_TIMELINE), latestConfiguration);
      },
    });

    return timeline;
  }

  AutoImageColorUpdate(configuration: MovingTargetTaskConfiguration, scene: Phaser.Scene) {
    const timeline = scene.tweens.createTimeline();
    const follower = { t: 0, vec: new Phaser.Math.Vector2() };

    timeline.add({
      targets: follower,
      t: { from: 0, to: 1 },
      duration: CONSTANTS.movingTarget.colourRateUpdate,
      repeat: -1,
      onRepeat: () => {
        this.UpdateColorOfAutoImage(configuration, scene);
      },
    });

    return timeline;
  }

  GetTrackerPosition(scene: Phaser.Scene): Phaser.Math.Vector2 {
    if (this.isActive) {
      return new Phaser.Math.Vector2(this.image?.x, this.image?.y);
    } else {
      return this.GetCenterPosition(scene);
    }
  }

  GetCenterPosition(scene: Phaser.Scene): Phaser.Math.Vector2 {
    const width: any = scene.game.canvas.width;
    const height: any = scene.game.canvas.height;

    const x = width / 2;
    const y = height / 2;
    return new Phaser.Math.Vector2(x, y);
  }

  GetCurrenScreenSize(scene: Phaser.Scene) {
    const width: number = scene.game.canvas.width;
    let height: number = width * CONSTANTS.movingTarget.heightPercent; // Maintain aspect ratio (height = 60% of width)

    height = Math.min(height, scene.game.canvas.height); // Ensure height does not exceed the actual canvas height

    return { width, height };
  }

  CenterImagePosition(scene: Phaser.Scene) {
    let center = this.GetCenterPosition(scene);
    return this.image?.setPosition(center.x, center.y);
  }

  private CreateMovementTween(
    configuration: MovingTargetTaskConfiguration,
    scene: Phaser.Scene
  ): Phaser.Tweens.Timeline {
    let timeline;

    switch (configuration.SphereTrajectoryOption) {
      case SphereTrajectoryOption.Linear:
        timeline = this.CreateLinearTween(configuration, scene);
        break;
      case SphereTrajectoryOption.Ellipse:
        timeline = this.CreateEllipseTween(configuration, scene);
        break;
      case SphereTrajectoryOption.Bouncing:
        timeline = this.CreateBouncingTween(configuration, scene);
        break;
      case SphereTrajectoryOption.Horizontal8:
        timeline = this.CreateHorizontalEightTween(configuration, scene);
        break;
      default: {
        timeline = scene.tweens.createTimeline();
      }
    }

    this.UpdateSpeedForTimeline(timeline, configuration);

    return timeline;
  }

  public RebuildAfterResize(scene: Phaser.Scene) {
    const { width, height } = this.GetCurrenScreenSize(scene);
    this.CenterImagePosition(scene);
    if (this.image) {
      this.image.setScale(this.CalculateScalingFactor(width * 0.85, height * 0.85));
    }

    const movement = this.CreateMovementTween(this.configuration, scene);
    this.ReplaceTimeline(MovingTargetTask.MOVEMENT_TIMELINE, movement);
  }

  private CalculatePixelPerMilliSecond(scene: Phaser.Scene) {
    const { width, height } = this.GetCurrenScreenSize(scene);
    const left = Number(width) * CONSTANTS.movingTarget.linearMaxLeft;
    const right = Number(width) * CONSTANTS.movingTarget.linearMaxRight;
    let trajectory = this.configuration.SphereTrajectoryOption;
    let path = new Phaser.Curves.Path();

    path.moveTo(left, 0);
    path.lineTo(right, trajectory === SphereTrajectoryOption.Linear ? undefined : height);

    return path.getLength() / CONSTANTS.movingTarget.timeForBottomLeftToTopRightAtDefaultSpeedInMilliseconds;
  }

  private CreateLinearTween(configuration: MovingTargetTaskConfiguration, scene: Phaser.Scene): Phaser.Tweens.Timeline {
    const { width } = this.GetCurrenScreenSize(scene);
    const left = Number(width) * CONSTANTS.movingTarget.linearMaxLeft;
    const right = Number(width) * CONSTANTS.movingTarget.linearMaxRight;

    let timeline = scene.tweens.createTimeline();

    let center = this.GetCenterPosition(scene);
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };
    let follower1 = { t: 0, vec: new Phaser.Math.Vector2() };
    let path = new Phaser.Curves.Path(center.x, center.y);
    let path1 = new Phaser.Curves.Path(right, center.y);

    let pixelsPerMS = this.CalculatePixelPerMilliSecond(scene);

    path.moveTo(center.x, center.y);
    path.lineTo(right, center.y);

    path1.lineTo(left, center.y);

    timeline.add({
      targets: follower,
      duration: path.getLength() / pixelsPerMS,
      t: 1,
      ease: 'Cubic.easeInOut',
      onUpdate: () => {
        path.getPoint(follower.t, follower.vec);
        this.image?.setPosition(follower.vec.x, follower.vec.y);
      },
    });

    timeline.add({
      targets: follower1,
      duration: path1.getLength() / pixelsPerMS,
      t: 1,
      ease: 'Cubic.easeInOut',

      repeat: -1,
      yoyo: true,
      onUpdate: () => {
        path1.getPoint(follower1.t, follower1.vec);
        this.image?.setPosition(follower1.vec.x, follower1.vec.y);
      },
    });
    return timeline;
  }

  private CreateEllipseTween(
    configuration: MovingTargetTaskConfiguration,
    scene: Phaser.Scene
  ): Phaser.Tweens.Timeline {
    let timeline = scene.tweens.createTimeline();
    const { width, height } = this.GetCurrenScreenSize(scene);

    let radiusX = width * CONSTANTS.movingTarget.ellipseRadiusX;
    let radiusY = height * CONSTANTS.movingTarget.ellipseRadiusY;
    let center = this.GetCenterPosition(scene);
    let ellipse = new Phaser.Curves.Ellipse(center.x, center.y);
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };
    let clockWise = ellipse.clockwise;

    ellipse.setXRadius(radiusX);
    ellipse.setYRadius(radiusY);
    ellipse.getPoint(follower.t, follower.vec);

    ellipse.setClockwise(true);
    ellipse.setStartAngle(0);
    ellipse.setEndAngle(ellipse.startAngle + this.random.between(180, 360)); // this.random.between(180, 360)

    ellipse.updateArcLengths();

    let pixelsPerMS = this.CalculatePixelPerMilliSecond(scene);

    let length = ellipse.getLength();
    let duration = length / pixelsPerMS;
    this.image?.setPosition(follower.vec.x, follower.vec.y);

    timeline.add({
      targets: follower,
      t: 1,
      duration: duration,
      ease: 'linear',
      repeat: -1,
      onUpdate: tween => {
        ellipse.getPoint(follower.t, follower.vec);
        this.image?.setPosition(follower.vec.x, follower.vec.y);
      },
      onRepeat: tween => {
        clockWise = !clockWise;
        ellipse.setClockwise(clockWise);

        let angleIncrement = this.random.between(90, 345);
        //
        let newAngle = clockWise
          ? (ellipse.endAngle - angleIncrement) % 360
          : (ellipse.endAngle + angleIncrement) % 360;

        ellipse.startAngle = ellipse.endAngle;
        ellipse.endAngle = newAngle;

        ellipse.updateArcLengths();

        let newLength = ellipse.getLength();
        let newSpeed = 1 / (newLength / length);
        tween.setTimeScale(newSpeed);

        // Force reset it, because we get one more Update after Repeat
        follower.t = 0;
      },
    });

    return timeline;
  }

  private CreateBouncingTween(
    configuration: MovingTargetTaskConfiguration,
    scene: Phaser.Scene
  ): Phaser.Tweens.Timeline {
    let timeline = scene.tweens.createTimeline();
    let prevRandomNumber: undefined | number = undefined;
    const { width, height } = this.GetCurrenScreenSize(scene);

    let center = this.GetCenterPosition(scene);
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };
    let path = new Phaser.Curves.Path(center.x, center.y);

    let ellipse = new Phaser.Curves.Ellipse(center.x, center.y);
    ellipse.setXRadius(width / 2.2);
    ellipse.setYRadius(height / 2.6);

    let pixelsPerMS = this.CalculatePixelPerMilliSecond(scene);

    this.pointOnEllipse = this.random.random();
    let randomPointOuter = ellipse.getPoint(this.pointOnEllipse);
    path.lineTo(randomPointOuter.x, randomPointOuter.y);

    let originalDuration = path.getLength() / pixelsPerMS;

    timeline.add({
      targets: follower,
      t: { from: 0, to: 1 },
      ease: 'Cubic.easeInOut',
      duration: originalDuration,
      repeat: -1,
      onUpdate: tween => {
        path.getPoint(follower.t, follower.vec);
        this.image?.setPosition(follower.vec.x, follower.vec.y);
      },
      onStart: tween => {
        let newDuration = path.getLength() / pixelsPerMS;
        tween.setTimeScale(originalDuration / newDuration);
      },
      onRepeat: (tween: any) => {
        if (!this.image) return;
        // clear path
        path.curves.length = 0;
        path.cacheLengths.length = 0;

        path.moveTo(this.image.x, this.image.y);

        let random = this.random.randomBouncingAngle(prevRandomNumber);
        prevRandomNumber = random;
        this.pointOnEllipse = random;

        let randomPoint = ellipse.getPoint(this.pointOnEllipse);

        path.lineTo(randomPoint.x, randomPoint.y);

        let newDuration = path.getLength() / pixelsPerMS;

        tween.setTimeScale(originalDuration / newDuration);

        // Repeat gets called BEFORE update for 100%, so we need to manually force the restart.
        tween.restart();
      },
    });

    return timeline;
  }

  private CreateHorizontalEightTween(configuration: MovingTargetTaskConfiguration, scene: Phaser.Scene) {
    let timeline = scene.tweens.createTimeline();

    let center = this.GetCenterPosition(scene);
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };
    let path = new Phaser.Curves.Path(center.x, center.y);
    const { width, height } = this.GetCurrenScreenSize(scene);

    const horizontalEightRadius =
      Math.sqrt(width * width + height * height) * CONSTANTS.movingTarget.horizontalEightRadius;
    const verticalEightRadius = horizontalEightRadius * CONSTANTS.movingTarget.horizontalEightHeightScale;

    path.ellipseTo(horizontalEightRadius, verticalEightRadius);
    path.moveTo(center.x, center.y);
    path.ellipseTo(
      horizontalEightRadius,
      verticalEightRadius,
      0,
      360,
      true,
      CONSTANTS.movingTarget.horizontalEightRadiusRotation
    );
    let pixelsPerMS = this.CalculatePixelPerMilliSecond(scene);
    let originalDuration = path.getLength() / pixelsPerMS;

    timeline.add({
      targets: follower,
      t: 1,
      ease: 'linear',
      duration: originalDuration,
      repeat: -1,
      onUpdate: () => {
        path.getPoint(follower.t, follower.vec);
        this.image?.setPosition(follower.vec.x, follower.vec.y);
      },
    });

    return timeline;
  }

  private CreateSnapOutTween(
    configuration: MovingTargetTaskConfiguration,
    scene: Phaser.Scene
  ): Phaser.Tweens.Timeline {
    const height: number | string = scene.game.canvas.height;
    const top = Number(height) * CONSTANTS.movingTarget.snapOutTop;
    const bottom = Number(height) * CONSTANTS.movingTarget.snapOutBottom;

    let duration = configuration.baseSpeed;

    let timeline = scene.tweens.createTimeline();

    let center = this.GetCenterPosition(scene);
    let follower = { t: 0, vec: new Phaser.Math.Vector2() };
    let follower1 = { t: 0, vec: new Phaser.Math.Vector2() };
    let path = new Phaser.Curves.Path(center.x, center.y);
    let path1 = new Phaser.Curves.Path(center.x, top);

    path.moveTo(center.x, center.y);
    path.lineTo(center.x, top);

    path1.lineTo(center.x, bottom);

    timeline.add({
      targets: follower,
      duration: duration,
      t: 1,
      ease: 'Cubic.easeInOut',
      onUpdate: () => {
        path.getPoint(follower.t, follower.vec);
        this.image?.setPosition(follower.vec.x, follower.vec.y);
      },
    });

    timeline.add({
      targets: follower1,
      duration: duration,
      t: 1,
      ease: 'Cubic.easeInOut',

      repeat: -1,
      yoyo: true,
      onUpdate: () => {
        path1.getPoint(follower1.t, follower1.vec);
        this.image?.setPosition(follower1.vec.x, follower1.vec.y);
      },
    });
    return timeline;
  }
}
